import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/funnel';
import { isWelcomeOfferActive, welcomeOffer } from '@/data/workbooks';

interface SampleSignupProps {
  /** Where the signup happened — stored with the address. */
  source: string;
  /** Visual weight. `panel` is the full block, `inline` sits inside other cards. */
  variant?: 'panel' | 'inline' | 'dark';
  /** Unique id for the input label. */
  id?: string;
  heading?: string;
  description?: string;
}

/**
 * Email capture in exchange for the free sample pack. The address is stored
 * through the `subscribe` edge function, which also sends the sample.
 */
export const SampleSignup = ({
  source,
  variant = 'panel',
  id = 'sample-email',
  heading = 'Four pages, free, before you decide',
  description = 'One page each from the readiness audit, the rig scoring sheet, the twelve-month runway and the possession audit. Unedited, print-ready, sent straight to your inbox.',
}: SampleSignupProps) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const dark = variant === 'dark';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    try {
      const { data, error } = await supabase.functions.invoke('subscribe', {
        body: { email: email.trim(), source, wantsSample: true },
      });
      if (error || (data as { error?: string })?.error) {
        throw new Error((data as { error?: string })?.error ?? 'Request failed');
      }
      track('sample_requested', { source });
      setState('done');
      setMessage(
        (data as { sampleSent?: boolean })?.sampleSent
          ? 'Sent. Check your inbox — the download link is inside, and it stays live for fourteen days.'
          : 'You are on the list. The sample pack follows by email shortly.',
      );
      setEmail('');
    } catch {
      setState('error');
      setMessage('That did not go through. Try again in a moment.');
    }
  };

  const muted = dark
    ? 'text-ink-surface-foreground/70'
    : 'text-muted-foreground/85';

  if (state === 'done') {
    return (
      <div className={variant === 'panel' ? 'py-6' : ''}>
        <p className="text-[10px] tracking-luxury uppercase text-accent mb-2">
          On its way
        </p>
        <p className={`text-sm font-light leading-relaxed ${muted}`}>{message}</p>
      </div>
    );
  }

  const form = (
    <>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md">
        <label htmlFor={id} className="sr-only">
          Email address for the free sample pack
        </label>
        <input
          id={id}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className={
            dark
              ? 'flex-1 px-4 py-3 border border-ink-surface-foreground/25 bg-transparent text-ink-surface-foreground text-sm placeholder:text-ink-surface-foreground/50 focus:outline-none focus:border-foil transition-colors duration-500'
              : 'flex-1 px-4 py-3 border border-border/40 bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-accent/50 transition-colors duration-500'
          }
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className="btn-luxe px-6 py-3 text-[10px] tracking-luxury uppercase disabled:opacity-60"
        >
          {state === 'sending' ? 'Sending' : 'Send the sample'}
        </button>
      </form>
      {isWelcomeOfferActive() && (
        <p className={`text-[11px] font-light mt-3 ${muted}`}>
          The same email carries a {welcomeOffer.percentOff}% code for a single title, valid
          until {welcomeOffer.endsLabel}.
        </p>
      )}
      <p className={`text-[11px] font-light mt-3 ${muted}`}>
        You also get the monthly dispatch. One email, unsubscribe with a single reply.
      </p>
      {state === 'error' && (
        <p className="text-[11px] font-light mt-2 text-destructive">{message}</p>
      )}
    </>
  );

  if (variant !== 'panel') return <div>{form}</div>;

  return (
    <section className="py-12 md:py-16">
      <div className="luxe-card max-w-2xl mx-auto p-8 md:p-12">
        <span className="text-[10px] tracking-luxury uppercase text-accent/80 block mb-4">
          The sample pack
        </span>
        <h3 className="font-serif text-2xl md:text-3xl font-medium text-foreground mb-4 leading-snug">
          {heading}
        </h3>
        <p className="text-sm text-muted-foreground/90 font-light leading-relaxed mb-8 max-w-md">
          {description}
        </p>
        {form}
      </div>
    </section>
  );
};
